import React from 'react';
import { usePendingTransactions, PendingTransaction } from '@/hooks/use-currency';
import { format } from 'date-fns';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Clock, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLocation } from 'wouter';

const statusIcons = {
  pending: <Clock className="h-4 w-4 text-yellow-400" />,
  completed: <CheckCircle className="h-4 w-4 text-green-400" />,
  failed: <XCircle className="h-4 w-4 text-red-400" />
};

const statusColors = {
  pending: 'bg-yellow-900/20 text-yellow-300 border-yellow-700',
  completed: 'bg-green-900/20 text-green-300 border-green-700',
  failed: 'bg-red-900/20 text-red-300 border-red-700'
};

export function PendingTransactions() {
  const [_, setLocation] = useLocation();
  const { data: pendingTransactions, isLoading, isError } = usePendingTransactions();

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <RefreshCw className="h-5 w-5" />
          <h2 className="text-xl font-semibold ml-2">Payment History</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2].map((i) => (
            <Card key={i} className="border border-gray-700">
              <CardHeader>
                <Skeleton className="h-5 w-3/4 bg-gray-700" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full bg-gray-700 mb-2" />
                <Skeleton className="h-4 w-3/4 bg-gray-700" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <RefreshCw className="h-5 w-5" />
          <h2 className="text-xl font-semibold ml-2">Payment History</h2>
        </div>
        
        <div className="rounded-md bg-red-900/20 border border-red-700 p-4 text-red-200 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>Failed to load payment history</span>
        </div>
      </div>
    );
  }

  // Ensure we always have an array to map over, even if data is undefined
  const transactions = pendingTransactions ?? [];
  
  if (transactions.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center mb-4">
          <RefreshCw className="h-5 w-5" />
          <h2 className="text-xl font-semibold ml-2">Payment History</h2>
        </div>
        
        <div className="text-center p-6 border border-gray-700 rounded-md bg-gray-800/50">
          <RefreshCw className="h-12 w-12 mx-auto mb-4 text-gray-500" />
          <p className="text-gray-400">No payment history available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center mb-4">
        <RefreshCw className="h-5 w-5" />
        <h2 className="text-xl font-semibold ml-2">Payment History</h2>
      </div>
      
      <motion.div 
        className="grid grid-cols-1 md:grid-cols-2 gap-4"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        {transactions.map((transaction: PendingTransaction) => (
          <motion.div 
            key={transaction.id} 
            variants={itemVariants}
            onClick={() => setLocation(`/shop/payment-callback?reference=${transaction.reference}`)}
            className="cursor-pointer transition-transform hover:scale-[1.02]"
          >
            <Card className="border border-gray-700 overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-base">
                    Payment {transaction.reference.substring(0, 8)}...
                  </CardTitle>
                  <Badge 
                    variant="outline" 
                    className={`flex items-center gap-1 ${statusColors[transaction.status]}`}
                  >
                    {statusIcons[transaction.status]}
                    {transaction.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Amount:</span>
                    <span className="font-medium">₵{(transaction.amount).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Coins:</span>
                    <span className={`font-medium ${transaction.coinType === 'premium' ? 'text-purple-400' : 'text-yellow-400'}`}>
                      {transaction.coins} {transaction.coinType === 'premium' ? 'Gems' : 'Coins'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Date:</span>
                    <span className="font-medium">{format(new Date(transaction.createdAt), 'MMM d, yyyy • h:mm a')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Email:</span>
                    <span className="font-medium truncate max-w-[150px]">{transaction.email}</span>
                  </div>
                </div>
                
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </motion.div>
    </div>
  );
}